import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Settings, DollarSign, Clock, Save, Users, Plus, Edit, Trash2, Loader2, Satellite, Eye, EyeOff, FileText, CheckCircle, XCircle, Image, Building } from 'lucide-react';
import { getConfig, updateConfig, getTecnicos, factoStatus, uploadGiroLogo } from '../../lib/gestion4satApi';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const G4S_ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'tecnico', label: 'T\u00e9cnico' },
  { value: 'comercial', label: 'Comercial / Facturaci\u00f3n' },
  { value: 'financiero', label: 'Financiero / Conciliaci\u00f3n' },
  { value: 'auditor', label: 'Auditor / Conectividad' },
];

export default function G4sConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [precios, setPrecios] = useState({});
  const [serviciosCustom, setServiciosCustom] = useState([]);
  const [umbral, setUmbral] = useState(48);
  const [foursat, setFoursat] = useState({ email: '', password: '', api_hash: '' });
  const [showPass, setShowPass] = useState(false);
  const [testingCreds, setTestingCreds] = useState(false);
  const [tablaFontSize, setTablaFontSize] = useState('normal');

  const [facto, setFacto] = useState({
    client_id: '', client_secret: '', username: '', password: '', account_id: '',
    issuer_rut: '', issuer_razon_social: '', issuer_direccion: '', issuer_comuna: '',
    issuer_ciudad: '', issuer_giro: '', issuer_telefono: '',
  });
  const [factoTesting, setFactoTesting] = useState(false);
  const [factoOk, setFactoOk] = useState(null);
  const [giroProfiles, setGiroProfiles] = useState([]);
  const [giroDialog, setGiroDialog] = useState(null); // null or giro profile being edited
  const [giroUploading, setGiroUploading] = useState(false);

  // Users
  const [usuarios, setUsuarios] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({ nombre: '', email: '', password: '', rol: 'tecnico' });
  const [editUserId, setEditUserId] = useState(null);
  const [userSaving, setUserSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const c = await getConfig();
        setConfig(c);
        setPrecios(c.precios || {});
        setServiciosCustom(c.servicios_catalogo || []);
        setUmbral(c.umbral_offline_horas || 48);
        setFoursat(c.foursat_credentials || { email: '', password: '', api_hash: '' });
        setTablaFontSize(c.tabla_font_size || 'normal');
        if (c.facto) setFacto(prev => ({ ...prev, ...c.facto }));
        if (c.giro_profiles) setGiroProfiles(c.giro_profiles);
      } catch (e) { toast.error(e.message); }
      setLoading(false);
    };
    load();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const d = await getTecnicos();
      setUsuarios(d.tecnicos || []);
    } catch { /* ignore */ }
    setUsersLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateConfig({ precios, servicios_catalogo: serviciosCustom, umbral_offline_horas: umbral, foursat_credentials: foursat, tabla_font_size: tablaFontSize, facto, giro_profiles: giroProfiles });
      toast.success('Configuraci\u00f3n guardada');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const testFoursatCreds = async () => {
    if (!foursat.email || !foursat.password) {
      toast.error('Ingresa email y password de 4sat.cl');
      return;
    }
    setTestingCreds(true);
    try {
      const resp = await fetch('https://4sat.cl/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: foursat.email, password: foursat.password }),
      });
      const data = await resp.json();
      if (data.status === 1 && data.user_api_hash) {
        setFoursat({ ...foursat, api_hash: data.user_api_hash });
        toast.success('Conexi\u00f3n exitosa. API Hash obtenido.');
      } else {
        toast.error('Credenciales inv\u00e1lidas en 4sat.cl');
      }
    } catch (e) {
      toast.error('Error conectando a 4sat.cl');
    }
    setTestingCreds(false);
  };

  const openCreateUser = () => {
    setEditUserId(null);
    setUserForm({ nombre: '', email: '', password: '', rol: 'tecnico' });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId, nombre) => {
    if (!window.confirm(`¿Eliminar usuario "${nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/usuarios/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Error eliminando usuario');
      }
      toast.success(`Usuario "${nombre}" eliminado`);
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const openEditUser = (u) => {
    setEditUserId(u.id);
    setUserForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol });
    setShowUserForm(true);
  };

  const handleUserSubmit = async () => {
    if (!userForm.nombre || !userForm.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    setUserSaving(true);
    try {
      const token = localStorage.getItem('token');
      const hdrs = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      if (editUserId) {
        const body = { nombre: userForm.nombre, rol: userForm.rol };
        if (userForm.password) body.password = userForm.password;
        await fetch(`${API_URL}/api/usuarios/${editUserId}`, { method: 'PUT', headers: hdrs, body: JSON.stringify(body) });
        toast.success('Usuario actualizado');
      } else {
        if (!userForm.password || userForm.password.length < 6) {
          toast.error('Password m\u00ednimo 6 caracteres');
          setUserSaving(false);
          return;
        }
        const resp = await fetch(`${API_URL}/api/usuarios`, {
          method: 'POST', headers: hdrs,
          body: JSON.stringify({ nombre: userForm.nombre, email: userForm.email, password: userForm.password, rol: userForm.rol, modulos_acceso: ['gestion_4sat'] }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Error creando usuario');
        }
        toast.success('Usuario creado');
      }
      setShowUserForm(false);
      loadUsers();
    } catch (e) { toast.error(e.message); }
    setUserSaving(false);
  };

  if (loading) return <G4sLayout><Skeleton className="h-96 w-full" /></G4sLayout>;

  return (
    <G4sLayout>
      <div className="space-y-6 max-w-2xl" data-testid="g4s-config-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Configuraci&oacute;n</h1>
          <p className="text-sm text-muted-foreground">Par&aacute;metros del m&oacute;dulo Gesti&oacute;n 4sat</p>
        </div>

        {/* Precios */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-emerald-500" /> Cat&aacute;logo de Servicios y Precios
                </CardTitle>
                <CardDescription>Define servicios con precios netos (sin IVA) y observaciones</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => setServiciosCustom([...serviciosCustom, { nombre: '', precio: 0, tipo: 'mensual', observaciones: '' }])} data-testid="btn-agregar-servicio">
                <Plus className="h-3 w-3 mr-1" /> Servicio
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Default services (legacy) */}
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer mb-2">Precios base por defecto (usados si no hay cat&aacute;logo)</summary>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">GPS Solo (mensual)</Label>
                  <Input type="number" value={precios.gps_solo || 0} onChange={e => setPrecios({...precios, gps_solo: parseInt(e.target.value) || 0})} className="h-8 text-xs" data-testid="precio-gps-solo" />
                </div>
                <div>
                  <Label className="text-xs">GPS + Arriendo</Label>
                  <Input type="number" value={precios.gps_arriendo || 0} onChange={e => setPrecios({...precios, gps_arriendo: parseInt(e.target.value) || 0})} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">C&aacute;maras</Label>
                  <Input type="number" value={precios.camaras || 0} onChange={e => setPrecios({...precios, camaras: parseInt(e.target.value) || 0})} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Instalaci&oacute;n GPS</Label>
                  <Input type="number" value={precios.instalacion_gps || 0} onChange={e => setPrecios({...precios, instalacion_gps: parseInt(e.target.value) || 0})} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Instalaci&oacute;n C&aacute;maras</Label>
                  <Input type="number" value={precios.instalacion_camaras || 0} onChange={e => setPrecios({...precios, instalacion_camaras: parseInt(e.target.value) || 0})} className="h-8 text-xs" />
                </div>
              </div>
            </details>

            {/* Dynamic catalog */}
            {serviciosCustom.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Sin servicios personalizados. Haz click en + Servicio para agregar.
              </p>
            ) : (
              serviciosCustom.map((svc, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2 relative group">
                  <Button
                    size="icon" variant="ghost"
                    className="absolute top-2 right-2 h-6 w-6 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => setServiciosCustom(serviciosCustom.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs">Nombre del servicio</Label>
                      <Input
                        value={svc.nombre}
                        onChange={e => { const u = [...serviciosCustom]; u[i] = {...u[i], nombre: e.target.value}; setServiciosCustom(u); }}
                        placeholder="Ej: Plataforma rastreo GPS"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Precio neto ($)</Label>
                      <Input
                        type="number"
                        value={svc.precio || 0}
                        onChange={e => { const u = [...serviciosCustom]; u[i] = {...u[i], precio: parseInt(e.target.value) || 0}; setServiciosCustom(u); }}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select value={svc.tipo || 'mensual'} onValueChange={v => { const u = [...serviciosCustom]; u[i] = {...u[i], tipo: v}; setServiciosCustom(u); }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensual">Mensual</SelectItem>
                          <SelectItem value="unico">Pago &uacute;nico</SelectItem>
                          <SelectItem value="instalacion">Instalaci&oacute;n</SelectItem>
                          <SelectItem value="arriendo">Arriendo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Observaciones / Detalle</Label>
                      <Input
                        value={svc.observaciones || ''}
                        onChange={e => { const u = [...serviciosCustom]; u[i] = {...u[i], observaciones: e.target.value}; setServiciosCustom(u); }}
                        placeholder="Incluye X dispositivos, plan datos, etc."
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Umbrales */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-500" /> Umbrales de Alerta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label>Horas sin transmisi&oacute;n para alerta</Label>
              <div className="flex items-center gap-3">
                <Input type="number" value={umbral} onChange={e => setUmbral(parseInt(e.target.value) || 1)} className="w-24" data-testid="umbral-horas" />
                <span className="text-sm text-muted-foreground">horas</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integraci\u00f3n 4sat.cl */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="h-4 w-4 text-zinc-500" /> Tama&ntilde;o de Letra en Tablas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={tablaFontSize} onValueChange={setTablaFontSize}>
              <SelectTrigger className="w-48" data-testid="select-font-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Peque&ntilde;o (11px)</SelectItem>
                <SelectItem value="normal">Normal (12px)</SelectItem>
                <SelectItem value="large">Grande (14px)</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Integraci\u00f3n 4sat.cl */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Satellite className="h-4 w-4 text-blue-500" /> Integraci&oacute;n 4sat.cl
            </CardTitle>
            <CardDescription>Credenciales para sincronizar datos de scrapers a la plataforma 4sat.cl (campos QR del dispositivo)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Email 4sat.cl</Label>
                <Input
                  type="email"
                  value={foursat.email || ''}
                  onChange={e => setFoursat({...foursat, email: e.target.value})}
                  placeholder="admin@4sat.cl"
                  data-testid="foursat-email"
                />
              </div>
              <div>
                <Label>Password</Label>
                <div className="flex gap-2">
                  <Input
                    type={showPass ? 'text' : 'password'}
                    value={foursat.password || ''}
                    onChange={e => setFoursat({...foursat, password: e.target.value})}
                    placeholder="••••••••"
                    data-testid="foursat-password"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setShowPass(!showPass)}>
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <Label>API Hash (se obtiene autom&aacute;ticamente al probar conexi&oacute;n)</Label>
              <Input
                value={foursat.api_hash || ''}
                onChange={e => setFoursat({...foursat, api_hash: e.target.value})}
                placeholder="Se genera al probar conexión"
                className="font-mono text-xs"
                data-testid="foursat-api-hash"
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={testFoursatCreds}
                disabled={testingCreds}
                data-testid="btn-test-foursat"
              >
                {testingCreds ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Satellite className="h-4 w-4 mr-2" />}
                Probar Conexi&oacute;n
              </Button>
              {foursat.api_hash && (
                <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 text-xs">
                  Conectado
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Perfiles de Giro */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="h-4 w-4 text-purple-500" /> Perfiles de Giro
            </CardTitle>
            <CardDescription>Define giros con logo y datos para personalizar emisión de DTEs y emails</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {giroProfiles.length === 0 && (
              <p className="text-sm text-muted-foreground">Sin perfiles de giro. El emisor usará los datos por defecto de Facto.</p>
            )}
            {giroProfiles.map((g, i) => (
              <div key={g.id} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/20">
                {g.logo_url ? (
                  <img src={g.logo_url.startsWith('http') ? g.logo_url : `${process.env.REACT_APP_BACKEND_URL}${g.logo_url}`} alt="" className="h-10 w-10 rounded object-cover border" />
                ) : (
                  <div className="h-10 w-10 rounded bg-muted flex items-center justify-center"><Image className="h-5 w-5 text-muted-foreground" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{g.nombre}</div>
                  <div className="text-xs text-muted-foreground truncate">{g.giro}</div>
                  {g.email_facturacion && <div className="text-xs text-blue-600">{g.email_facturacion}</div>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setGiroDialog({ ...g, _index: i })}><Edit className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-500"
                  onClick={() => { setGiroProfiles(prev => prev.filter((_, idx) => idx !== i)); toast.info('Perfil eliminado, guarda para aplicar'); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline"
              onClick={() => setGiroDialog({ id: crypto.randomUUID().slice(0,8), nombre: '', giro: '', razon_social: '', direccion: '', comuna: '', ciudad: '', telefono: '', email_facturacion: '', logo_url: '', _index: -1 })}
              data-testid="btn-add-giro"
            >
              <Plus className="h-4 w-4 mr-1" /> Agregar Perfil de Giro
            </Button>
          </CardContent>
        </Card>

        {/* Giro Dialog */}
        <Dialog open={!!giroDialog} onOpenChange={() => setGiroDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{giroDialog?._index === -1 ? 'Nuevo' : 'Editar'} Perfil de Giro</DialogTitle>
            </DialogHeader>
            {giroDialog && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {giroDialog.logo_url ? (
                    <img src={giroDialog.logo_url.startsWith('http') ? giroDialog.logo_url : `${process.env.REACT_APP_BACKEND_URL}${giroDialog.logo_url}`} alt="" className="h-16 w-16 rounded object-cover border" />
                  ) : (
                    <div className="h-16 w-16 rounded bg-muted flex items-center justify-center border"><Image className="h-8 w-8 text-muted-foreground" /></div>
                  )}
                  <div>
                    <Label className="text-xs">Logo (PNG/JPG, max 500KB)</Label>
                    <Input type="file" accept="image/png,image/jpeg,image/webp" className="h-8 text-xs"
                      disabled={giroUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setGiroUploading(true);
                        try {
                          const r = await uploadGiroLogo(file);
                          setGiroDialog(prev => ({ ...prev, logo_url: r.url }));
                          toast.success('Logo subido');
                        } catch (err) { toast.error(err.message); }
                        setGiroUploading(false);
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nombre del perfil</Label>
                    <Input value={giroDialog.nombre || ''} onChange={e => setGiroDialog(p => ({...p, nombre: e.target.value}))} placeholder="4SAT GPS" className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Giro (actividad SII)</Label>
                    <Input value={giroDialog.giro || ''} onChange={e => setGiroDialog(p => ({...p, giro: e.target.value}))} placeholder="Servicios de rastreo GPS" className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Razón Social (si difiere)</Label>
                    <Input value={giroDialog.razon_social || ''} onChange={e => setGiroDialog(p => ({...p, razon_social: e.target.value}))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Dirección</Label>
                    <Input value={giroDialog.direccion || ''} onChange={e => setGiroDialog(p => ({...p, direccion: e.target.value}))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Comuna</Label>
                    <Input value={giroDialog.comuna || ''} onChange={e => setGiroDialog(p => ({...p, comuna: e.target.value}))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Ciudad</Label>
                    <Input value={giroDialog.ciudad || ''} onChange={e => setGiroDialog(p => ({...p, ciudad: e.target.value}))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Teléfono</Label>
                    <Input value={giroDialog.telefono || ''} onChange={e => setGiroDialog(p => ({...p, telefono: e.target.value}))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Email Facturación</Label>
                    <Input value={giroDialog.email_facturacion || ''} onChange={e => setGiroDialog(p => ({...p, email_facturacion: e.target.value}))} placeholder="facturacion@empresa.cl" className="h-8 text-xs" />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setGiroDialog(null)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  if (!giroDialog?.nombre || !giroDialog?.giro) { toast.error('Nombre y Giro son requeridos'); return; }
                  const { _index, ...profile } = giroDialog;
                  if (_index === -1) {
                    setGiroProfiles(prev => [...prev, profile]);
                  } else {
                    setGiroProfiles(prev => prev.map((g, i) => i === _index ? profile : g));
                  }
                  setGiroDialog(null);
                  toast.info('Perfil actualizado, guarda configuración para aplicar');
                }}
              >
                <Save className="h-4 w-4 mr-1" /> {giroDialog?._index === -1 ? 'Agregar' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Integración Facto (Koywe) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" /> Integración Facto (Facturación Electrónica)
            </CardTitle>
            <CardDescription>Credenciales de la API Koywe para emitir DTEs electrónicos al SII</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <p className="text-xs font-semibold text-muted-foreground">CREDENCIALES API</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Client ID</Label>
                  <Input value={facto.client_id || ''} onChange={e => setFacto({...facto, client_id: e.target.value})} placeholder="demo/001" className="h-8 text-xs font-mono" data-testid="facto-client-id" />
                </div>
                <div>
                  <Label className="text-xs">Client Secret</Label>
                  <Input type="password" value={facto.client_secret || ''} onChange={e => setFacto({...facto, client_secret: e.target.value})} placeholder="••••" className="h-8 text-xs" data-testid="facto-client-secret" />
                </div>
                <div>
                  <Label className="text-xs">Username (RUT/usuario)</Label>
                  <Input value={facto.username || ''} onChange={e => setFacto({...facto, username: e.target.value})} placeholder="76.999.999-K/apiuser" className="h-8 text-xs font-mono" data-testid="facto-username" />
                </div>
                <div>
                  <Label className="text-xs">Password</Label>
                  <Input type="password" value={facto.password || ''} onChange={e => setFacto({...facto, password: e.target.value})} placeholder="••••" className="h-8 text-xs" data-testid="facto-password" />
                </div>
                <div>
                  <Label className="text-xs">Account ID</Label>
                  <Input value={facto.account_id || ''} onChange={e => setFacto({...facto, account_id: e.target.value})} placeholder="423" className="h-8 text-xs font-mono" data-testid="facto-account-id" />
                </div>
              </div>
            </div>

            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <p className="text-xs font-semibold text-muted-foreground">DATOS EMISOR (aparecen en los DTEs)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">RUT Emisor</Label>
                  <Input value={facto.issuer_rut || ''} onChange={e => setFacto({...facto, issuer_rut: e.target.value})} placeholder="76.399.932-7" className="h-8 text-xs font-mono" data-testid="facto-issuer-rut" />
                </div>
                <div>
                  <Label className="text-xs">Razón Social</Label>
                  <Input value={facto.issuer_razon_social || ''} onChange={e => setFacto({...facto, issuer_razon_social: e.target.value})} placeholder="4SAT GPS SpA" className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Dirección</Label>
                  <Input value={facto.issuer_direccion || ''} onChange={e => setFacto({...facto, issuer_direccion: e.target.value})} placeholder="Av. Providencia 1234" className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Comuna</Label>
                  <Input value={facto.issuer_comuna || ''} onChange={e => setFacto({...facto, issuer_comuna: e.target.value})} placeholder="Providencia" className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Ciudad</Label>
                  <Input value={facto.issuer_ciudad || ''} onChange={e => setFacto({...facto, issuer_ciudad: e.target.value})} placeholder="Santiago" className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Giro</Label>
                  <Input value={facto.issuer_giro || ''} onChange={e => setFacto({...facto, issuer_giro: e.target.value})} placeholder="Servicios de rastreo GPS" className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Teléfono</Label>
                  <Input value={facto.issuer_telefono || ''} onChange={e => setFacto({...facto, issuer_telefono: e.target.value})} placeholder="+56912345678" className="h-8 text-xs" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                type="button" variant="outline" size="sm"
                disabled={factoTesting}
                onClick={async () => {
                  setFactoTesting(true);
                  try {
                    // Save first then test
                    await updateConfig({ facto });
                    const r = await factoStatus();
                    setFactoOk(r.connected);
                    if (r.connected) {
                      toast.success(`Facto conectado: ${r.company || r.account_id} | ${r.document_types_count} tipos de DTE`);
                    } else {
                      toast.error(r.error || r.message || 'Error al conectar');
                    }
                  } catch (e) { toast.error(e.message); setFactoOk(false); }
                  setFactoTesting(false);
                }}
                data-testid="btn-test-facto"
              >
                {factoTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Probar Conexión Facto
              </Button>
              {factoOk === true && <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle className="h-3 w-3 mr-1" /> Conectado</Badge>}
              {factoOk === false && <Badge className="bg-red-500/10 text-red-600 border-red-500/20"><XCircle className="h-3 w-3 mr-1" /> Error</Badge>}
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar-config">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Configuraci\u00f3n'}
        </Button>

        {/* Usuarios */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" /> Usuarios del M&oacute;dulo
              </CardTitle>
              <Button size="sm" onClick={openCreateUser} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-nuevo-usuario">
                <Plus className="h-4 w-4 mr-1" /> Nuevo
              </Button>
            </div>
            <CardDescription>Administra usuarios y roles para Gesti&oacute;n 4sat</CardDescription>
          </CardHeader>
          <CardContent>
            {usersLoading ? <Skeleton className="h-20 w-full" /> : (
              <div className="space-y-2">
                {usuarios.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30">
                    <div>
                      <div className="text-sm font-medium">{u.nombre}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{G4S_ROLES.find(r => r.value === u.rol)?.label || u.rol}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => openEditUser(u)} title="Editar"><Edit className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteUser(u.id, u.nombre)} title="Eliminar"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
                {usuarios.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sin usuarios</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Form Dialog */}
        <Dialog open={showUserForm} onOpenChange={setShowUserForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editUserId ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
              <DialogDescription>
                {editUserId ? 'Modifica los datos del usuario del módulo 4sat' : 'Crea un nuevo usuario con acceso al módulo 4sat'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input value={userForm.nombre} onChange={e => setUserForm({...userForm, nombre: e.target.value})} data-testid="input-user-nombre" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} disabled={!!editUserId} data-testid="input-user-email" />
              </div>
              <div>
                <Label>{editUserId ? 'Nueva Password (dejar vac\u00edo para mantener)' : 'Password'}</Label>
                <Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} data-testid="input-user-pass" />
              </div>
              <div>
                <Label>Rol</Label>
                <Select value={userForm.rol} onValueChange={v => setUserForm({...userForm, rol: v})}>
                  <SelectTrigger data-testid="select-user-rol"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {G4S_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserForm(false)}>Cancelar</Button>
              <Button onClick={handleUserSubmit} disabled={userSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar-usuario">
                {userSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editUserId ? 'Guardar' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </G4sLayout>
  );
}
